import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  BookOpen, Package, Building2, Sparkles, MessageCircle, Volume2,
  ShieldCheck, Users, CreditCard, ArrowUpRight,
} from "lucide-react";

const OFFERINGS = [
  {
    icon: BookOpen,
    tag: "Books · Kindle",
    title: "The doctrine in print.",
    body: "Two Kindle books available on Amazon: the Work Book (exercises & prompts) and the Manual — Guide to Healing (the foundational doctrine). Everything Amazon ships, worldwide.",
    cta: { to: "/books", label: "Read the books" },
  },
  {
    icon: Package,
    tag: "The 8 Modules · Video course",
    title: "Eight guided sessions. Rich on camera.",
    body: "01 Understanding God. 02 Accepting Where You Are. 03 Embracing Your Journey. 04 The Wall of Adversity. 05 The Decision. 06 Knowledge — Facing Your Past. 07 Wisdom — Processing Your Pain. 08 Understanding — Becoming the Healer. Watched right here on tacc.love, lifetime access.",
    cta: { to: "/modules", label: "See the 8 modules" },
  },
  {
    icon: CreditCard,
    tag: "Three ways in",
    title: "Audio $37 · Course $57 · Bundle $77.",
    body: "Buy securely on the site with card, or grab it externally on Amazon / Gumroad. The 8 modules live inside the Complete Bundle at $77 — best value.",
    cta: { to: "/shop", label: "Open the shop" },
  },
  {
    icon: Volume2,
    tag: "Free · 90 seconds",
    title: "The Pre-Shift Ritual.",
    body: "A narrated, ninety-second grounding session on the homepage. Listen in your car, in the stairwell, or at your locker before the first task of the day. No sign-in required.",
    cta: { to: "/", label: "Play the ritual" },
  },
  {
    icon: Sparkles,
    tag: "AI · GPT-5.4",
    title: "Ask T.A.C.C. — your healing coach.",
    body: "A floating chat companion on every page, trained on the doctrine. Type what is heavy — a hard shift, a difficult colleague, a sleepless night — and get a stoic, grounded, actionable response in under 120 words. Not a therapist; a field manual you can talk to.",
    cta: { to: "/", label: "Open the coach" },
  },
  {
    icon: Users,
    tag: "Members Area",
    title: "Your dashboard. Your library.",
    body: "Create an account (email/password or Google) to access your purchased modules, join the Discord, and follow the practice. Purchases auto-unlock — even if you bought as a guest and signed up later with the same email.",
    cta: { to: "/dashboard", label: "Go to dashboard" },
  },
  {
    icon: Building2,
    tag: "For Companies",
    title: "License T.A.C.C. for your workforce.",
    body: "Corporate wellness that respects the weight your people already carry. Starter ($1,200/qtr, up to 25), Team ($4,800/qtr, 25–250), Enterprise (custom). Bulk licenses, live workshops, manager training, anonymous impact dashboard. Pay online or request a tailored proposal — one business day response.",
    cta: { to: "/b2b", label: "For companies" },
  },
  {
    icon: MessageCircle,
    tag: "Community",
    title: "The Discord.",
    body: "Members-only channels, live sessions, and accountability. Free to join when you become a member.",
    cta: { to: "/dashboard", label: "Join community" },
  },
  {
    icon: ShieldCheck,
    tag: "How it works",
    title: "Secure, respectful, and yours to keep.",
    body: "Payments processed by Stripe. Sign in with Google or email/password. Content unlocks on your dashboard automatically after purchase. If you are in crisis, the coach will direct you to 988. This work is a supplement, not a replacement for medical care.",
    cta: null,
  },
];

const item = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.04, ease: [0.65, 0, 0.35, 1] } }),
};

export default function AboutPage() {
  return (
    <main className="pt-32 md:pt-40 pb-28" data-testid="about-page">
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
          className="grid grid-cols-12 gap-y-10 items-end"
        >
          <div className="col-span-12 md:col-span-8">
            <div className="section-tag mb-6">§ What we do · The full picture</div>
            <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.88] tracking-tighter">
              Everything <span className="italic text-[#B45309]">T.A.C.C.</span> does.
            </h1>
          </div>
          <div className="col-span-12 md:col-span-4 flex items-end">
            <p className="text-[#78716C] text-lg leading-relaxed">
              T.A.C.C. — Through Adversity Comes Change — is a field manual for workers who show up to hard days carrying invisible weight. Books, modules, guided audio, an AI coach, a community, and a corporate wellness practice — all in one place.
            </p>
          </div>
        </motion.div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {OFFERINGS.map((o, i) => {
            const Icon = o.icon;
            return (
              <motion.article
                key={o.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={item}
                className="tacc-card col-span-12 md:col-span-6 p-8 md:p-10 flex flex-col"
                data-testid={`about-offering-${i}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <Icon size={24} className="text-[#B45309]" strokeWidth={1.4} />
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C]">
                    {o.tag}
                  </span>
                </div>
                <h3 className="font-display text-3xl md:text-4xl leading-tight tracking-tight mb-4">
                  {o.title}
                </h3>
                <p className="text-[#78716C] leading-relaxed mb-6 flex-1">
                  {o.body}
                </p>
                {o.cta && (
                  <Link
                    to={o.cta.to}
                    className="btn-outline text-sm w-fit"
                    data-testid={`about-cta-${i}`}
                  >
                    {o.cta.label}
                    <ArrowUpRight size={16} />
                  </Link>
                )}
              </motion.article>
            );
          })}
        </div>

        <div className="mt-24 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="section-tag mb-6">§ One doctrine · One place</div>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">
              Ready to <span className="italic text-[#B45309]">begin</span>?
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end gap-3 flex-wrap">
            <Link to="/shop" className="btn-ember text-sm">Open the shop <ArrowUpRight size={16} /></Link>
            <Link to="/b2b#quote" className="btn-outline text-sm">For companies</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
